import { useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import { Helmet } from 'react-helmet-async';
import { TListePrix } from '../../utils/types/tables/TListePrix';
import { useQuery } from '@tanstack/react-query';
import { TPrix } from '../../utils/types/tables/TPrix';
import { TClientSap } from '../../utils/types/tables/TClientSap';
import TListePrixGrid from '../../components/grid/TListePrixGrid';
import TClientSapGrid from '../../components/grid/TClientSapGrid';
import TPrixGrid from '../../components/grid/TPrixGrid';
import { IClient } from '../../utils/types/IClient';
import { TArticle } from '../../utils/types/tables/TArticle';
import { IBranche } from '../../utils/types/IBranche';
import { TDevise } from '../../utils/types/tables/TDevise';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';
import { IconFileInvoice, IconTags, IconUserDollar } from '@tabler/icons-react';

const PrixPage = () => {
    const {
        data: clients = [],
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: articles = [],
        refetch: refetchArticles,
        isError: isArticlesError,
        error: articlesError,
    } = useQuery<TArticle[]>({
        queryKey: ['articles'],
        queryFn: async () => {
            const res = await apiClient.get('/api/articles');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des produits' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: branches = [],
        refetch: refetchBranches,
        isError: isBranchesError,
        error: branchesError,
    } = useQuery<IBranche[]>({
        queryKey: ['branches'],
        queryFn: async () => {
            const res = await apiClient.get('/api/branches/getBranches');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des branches' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: devises = [],
        refetch: refetchDevises,
        isError: isDevisesError,
        error: devisesError,
    } = useQuery<TDevise[]>({
        queryKey: ['devises'],
        queryFn: async () => {
            const res = await apiClient.get('/api/devises');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des devises' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: listePrixes = [],
        isLoading: isLoadingListePrixes,
        refetch: refetchListePrixes,
        isError: isListePrixesError,
        error: listePrixesError,
    } = useQuery<TListePrix[]>({
        queryKey: ['listePrixes'],
        queryFn: async () => {
            const res = await apiClient.get('/api/prix/getListePrixes');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des listes de prix' },
        staleTime: 1000 * 60 * 60, // 1 hour
    });

    const {
        data: affectationsListes = [],
        isLoading: isLoadingAffectationsListes,
        refetch: refetchAffectationsListes,
        isError: isAffectationsListesError,
        error: affectationsListesError,
    } = useQuery<TClientSap[]>({
        queryKey: ['affectationsListes'],
        queryFn: async () => {
            const res = await apiClient.get('/api/prix/getAffectationsListes');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des affectations de listes de prix' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: configPrixes = [],
        isLoading: isLoadingConfigPrixes,
        refetch: refetchConfigPrixes,
        isError: isConfigPrixesError,
        error: configPrixesError,
    } = useQuery<TPrix[]>({
        queryKey: ['configPrixes'],
        queryFn: async () => {
            const res = await apiClient.get('/api/prix/getConfigPrixes');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des configurations des prix' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    // ---

    // void computed listePrixes

    const listePrixesComputed = useMemo(() => {
        return listePrixes.map((listePrix) => {
            return {
                ...listePrix,
            };
        });
    }, [listePrixes]);

    const affectationsListesComputed = useMemo(() => {
        return affectationsListes.map((client) => {
            return {
                ...client,
                nomClient: clients.find((c) => c.cardCode === client.codeClientSap)?.cardName ?? '',
                nomListePrix: listePrixes.find((lp) => lp.id === client.idListePrix)?.nom ?? '',
            };
        });
    }, [affectationsListes, clients, listePrixes]);

    const configPrixesComputed = useMemo(() => {
        return configPrixes.map((prix) => {
            return {
                ...prix,
                nomListe: listePrixes.find((lp) => lp.id === prix.idListe)?.nom ?? '',
                nomArticle: articles.find((a) => a.id === prix.idArticle)?.designation ?? '',
                nomBranche: branches.find((b) => b.id === prix.idBranche)?.nom ?? '',
                nomDevise: devises.find((d) => d.id === prix.idDevise)?.symbole ?? '',
            };
        });
    }, [configPrixes, listePrixes, articles, branches, devises]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isArticlesError, error: articlesError, title: 'Erreur lors du chargement des articles', refetch: refetchArticles },
            { isError: isBranchesError, error: branchesError, title: 'Erreur lors du chargement des branches', refetch: refetchBranches },
            { isError: isDevisesError, error: devisesError, title: 'Erreur lors du chargement des devises', refetch: refetchDevises },
            { isError: isListePrixesError, error: listePrixesError, title: 'Erreur lors du chargement des listes de prix', refetch: refetchListePrixes },
            { isError: isAffectationsListesError, error: affectationsListesError, title: 'Erreur lors du chargement des affectations listes de prix', refetch: refetchAffectationsListes },
            { isError: isConfigPrixesError, error: configPrixesError, title: 'Erreur lors du chargement des configurations de prix', refetch: refetchConfigPrixes },
        ],
        [isClientsError, clientsError, refetchClients, isArticlesError, articlesError, refetchArticles, isBranchesError, branchesError, refetchBranches, isDevisesError, devisesError, refetchDevises, isListePrixesError, listePrixesError, refetchListePrixes, isAffectationsListesError, affectationsListesError, refetchAffectationsListes, isConfigPrixesError, configPrixesError, refetchConfigPrixes],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Configuration de prix :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Système</div>
                            <h2 className="page-title">Configuration de prix</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="card-header">
                            <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs">
                                <li className="nav-item">
                                    <a href="#listePrixes" className="nav-link active" data-bs-toggle="tab">
                                        <IconFileInvoice className="icon me-2 icon-2" />
                                        Liste Prix
                                    </a>
                                </li>

                                <li className="nav-item">
                                    <a href="#affectationClientListePrix" className="nav-link" data-bs-toggle="tab">
                                        <IconUserDollar className="icon me-2 icon-2" />
                                        Affectation Client - Liste Prix
                                    </a>
                                </li>

                                <li className="nav-item">
                                    <a href="#configPrixes" className="nav-link" data-bs-toggle="tab">
                                        <IconTags className="icon me-2 icon-2" />
                                        Tarifs Listes Prix
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="card-body">
                            <div className="tab-content">
                                <div className="tab-pane active show" id="listePrixes">
                                    <TListePrixGrid listePrix={listePrixesComputed} isLoading={isLoadingListePrixes} />
                                </div>

                                <div className="tab-pane" id="affectationClientListePrix">
                                    <TClientSapGrid clients={affectationsListesComputed} isLoading={isLoadingAffectationsListes} />
                                </div>

                                <div className="tab-pane" id="configPrixes">
                                    <TPrixGrid prix={configPrixesComputed} isLoading={isLoadingConfigPrixes} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

PrixPage.Layout = DefaultLayout;

export default PrixPage;
